import { ShieldCheck, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useT } from '@/i18n';

interface ConsentBannerProps {
  /** 'proxy' — minor, parent must consent on their behalf.
   *  'verbal' — adult, capture verbal confirmation directly. */
  type: 'proxy' | 'verbal';
  /** Subject's full name (or placeholder) for the banner text interpolation. */
  subjectName: string;
  /** Called when the agent clicks confirm. Parent should flip the drawer
   *  state out of 'consent_pending' and into 'wizard_active'. */
  onConfirm: () => void;
}

/**
 * Cluster H — pre-wizard consent gate.
 *
 * Renders inside the SubScreeningDrawer body BEFORE the Stepper/DomainForm
 * become reachable. The drawer body is responsible for choosing the variant:
 *   - minor (age < 18 at capture)  → 'proxy'
 *   - adult                         → 'verbal'
 *
 * HIPAA: the banner enforces an explicit affirmative click before any PHI
 * fields appear. Per Pathway-HRSN minor consent policy, the proxy-consent
 * audit trail comes from the parent screening row (consent_given true at
 * promote-and-screen INSERT time). This banner is a UX gate, NOT a second
 * audit emit — the audit was already recorded when the sub was created.
 */
export function ConsentBanner({
  type,
  subjectName,
  onConfirm,
}: ConsentBannerProps): JSX.Element {
  const t = useT();
  const isProxy = type === 'proxy';

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <Alert variant="warning" role="alert" className="mb-4">
        {isProxy ? (
          <AlertCircle className="h-4 w-4" aria-hidden />
        ) : (
          <ShieldCheck className="h-4 w-4" aria-hidden />
        )}
        <AlertTitle>
          {isProxy
            ? t('subScreening.consent.proxyLabel')
            : t('subScreening.consent.verbalLabel')}
        </AlertTitle>
        <AlertDescription>
          {isProxy
            ? t('subScreening.consent.banner.proxy.text')
            : t('subScreening.consent.banner.verbal.text', { name: subjectName })}
        </AlertDescription>
      </Alert>

      <div className="flex justify-end">
        <Button onClick={onConfirm} size="lg">
          {t('subScreening.consent.confirmButton')}
        </Button>
      </div>
    </div>
  );
}
